import { useState } from 'react';
import toast, { Toaster } from 'react-hot-toast';
import { Portal } from '@headlessui/react';

import CloseCircle from '@/icons/duotone/close-circle.svg';
import TickCircle from '@/icons/duotone/tick-circle.svg';
import Copy from '@/icons/light/copy.svg';
import Share from '@/icons/light/share.svg';
import { Modal } from '@/atoms';

import { gtmPush } from '@/utils/gtm/helpers';

import type { FunctionComponent } from 'react';
import type { TypeModalShareProps } from './types';

export const ModalShare: FunctionComponent<TypeModalShareProps> = ({
  title,
  socialMedias,
  gtm,
  className,
}: TypeModalShareProps) => {
  const [isOpen, setIsOpen] = useState(false);
  // This is only supported in Secure Contexts (https)
  // @see https://developer.mozilla.org/en-US/docs/Mozilla/Add-ons/WebExtensions/Interact_with_the_clipboard#using_the_clipboard_api
  const copyLink = () => {
    if (navigator.clipboard) {
      return navigator.clipboard.writeText(window.location.href);
    } else {
      return new Promise((_, reject) => reject('Clipboard API not supported'));
    }
  };
  const success = () =>
    toast('El link se ha copiado correctamente.', {
      icon: <TickCircle width={64} color="#C4D600" />,
      duration: 1500,
      style: {
        background: '#F3F7CC',
        padding: '16px',
        fontWeight: 'bold',
      },
    });
  const error = () =>
    toast('Ha ocurrido un error al copiar el enlace al portapapeles.', {
      icon: <CloseCircle width={64} />,
      duration: 2000,
      style: {
        background: '#FFDCDE',
        padding: '16px',
        fontWeight: 'bold',
      },
    });

  const gtmShareModal = (method: string) => {
    gtmPush({
      event: 'share',
      method: method,
      content_type: 'url',
      item_id: gtm?.itemId,
    });
  };

  return (
    <>
      <button
        onClick={() => setIsOpen((previusState) => !previusState)}
        className={`flex items-center gap-2 text-primary-500 ${className}`}
      >
        Compartir <Share width={24} height={24} />
      </button>

      <Modal
        isOpen={isOpen}
        closeModal={() => setIsOpen(false)}
        className="md:w-[590px]"
        title={title}
      >
        <div className="grid grid-cols-2 justify-center gap-4 xs:mx-auto xs:max-w-max md:grid-flow-col md:grid-rows-1">
          {socialMedias.map((link, index: number) => (
            <a
              key={index}
              href={link.href}
              target="_blank"
              rel="noreferrer noopener"
              className="text-body text-body--m flex h-[92px] w-full flex-col items-center justify-center rounded-lg border border-solid border-neutrals-100 p-4 font-bold text-primary-500 xs:w-[115px] [&_span]:text-neutrals-900"
              onClick={() => {
                gtmShareModal(link.text);
              }}
            >
              {link.Icon}
              <span>{link.text}</span>
            </a>
          ))}

          <button
            onClick={() => {
              copyLink()
                .then(() => success())
                .catch(() => error())
                .finally(() => setIsOpen(false)),
                gtmShareModal('Copiar');
            }}
            className="text-body text-body--m flex h-[92px] w-full flex-col items-center justify-center rounded-lg border border-solid border-neutrals-100 p-4 font-bold text-primary-500 xs:w-[115px] [&_span]:text-neutrals-900"
          >
            <Copy width={32} height={32} />
            <span>Copiar</span>
          </button>
        </div>
      </Modal>

      <Portal>
        <Toaster position="bottom-center" />
      </Portal>
    </>
  );
};
ModalShare.displayName = 'ModalShare';
